import React from 'react';
import { AppStoreBadges, APP_STORE_URL, PLAY_STORE_URL } from '@coupling/ui';
import { existingPosNote, ownPosHeadline } from '../../content/siteMessaging';

type ProductMode = {
  kind: 'tap' | 'checkout' | 'booking';
  eyebrow: string;
  title: string;
  description: string;
  image: string;
  alt: string;
  width: number;
  height: number;
};

const productModes: ProductMode[] = [
  {
    kind: 'tap',
    eyebrow: 'On the phone you already have',
    title: 'Tap to Pay at the counter',
    description:
      'Take cards on your iPhone or Android. Coupling POS lives in the mobile app, so you can ring a sale on your phone or an optional Stripe reader.',
    image: '/screenshots/pos-e2e/07-iphone-pay-button.png',
    alt: 'Coupling mobile checkout on an iPhone, ready for the customer to pay',
    width: 1206,
    height: 2622,
  },
  {
    kind: 'checkout',
    eyebrow: 'Catalog, checkout, mixed tender',
    title: 'Sell the way your shop already sells',
    description:
      'Put items in a catalog, take the order, and split the ticket across cash, card, and rewards. Custom workflows, online orders, and tip pooling are built in.',
    image: '/screenshots/pos-e2e/02-accept-payment-entry-ipad.png',
    alt: 'Coupling POS payment entry showing a sale in progress',
    width: 1640,
    height: 2360,
  },
  {
    kind: 'booking',
    eyebrow: 'Appointments included',
    title: 'Let customers book the next visit',
    description:
      'Massage, chiropractic, and other appointment businesses can take bookings in the same system that handles checkout and rewards.',
    image: '/screenshots/pos-e2e/00-brian-dashboard-iphone.png',
    alt: 'Coupling mobile dashboard on an iPhone, the same app merchants use to run the shop',
    width: 1206,
    height: 2622,
  },
];

const cardClassName =
  'overflow-hidden rounded-[2rem] border border-brand-primary/15 bg-white shadow-[0_24px_60px_rgba(130,64,29,0.12)]';

export const ProductModesSection: React.FC = () => {
  return (
    <section id="product-modes" className="bg-white px-4 py-24">
      <div className="mx-auto max-w-6xl">
        <div className="max-w-3xl">
          <span className="inline-flex rounded-full bg-brand-primary/10 px-4 py-1 text-sm font-semibold text-brand-primary">
            What you can run tomorrow
          </span>
          <h2 className="mt-6 text-4xl font-bold tracking-tight text-neutral-900 sm:text-5xl">
            Checkout, booking, and rewards — on the phone you already have
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-neutral-600">
            {ownPosHeadline} for independent WNC shops that sell from a
            phone: farm stands, massage, chiropractic, and the stores ringing
            sales from a pocket, not a back office. Tap to Pay on your
            phone, or an optional Stripe reader.
          </p>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-3">
          {productModes.map((mode) => (
            <article key={mode.title} className={cardClassName}>
              <div className="bg-[radial-gradient(circle_at_top_left,_rgba(255,239,218,0.95),_transparent_35%),linear-gradient(135deg,#fff7ec_0%,#f8ead9_100%)] px-8 pt-8 pb-6">
                <div
                  data-testid="product-mode-device"
                  className="mx-auto h-64 w-44 overflow-hidden rounded-[1.75rem] border-[6px] border-neutral-900 bg-neutral-900 shadow-[0_18px_40px_rgba(23,10,5,0.22)] sm:h-72 sm:w-48"
                >
                  <img
                    src={mode.image}
                    alt={mode.alt}
                    width={mode.width}
                    height={mode.height}
                    className="h-full w-full rounded-[1.3rem] object-cover object-top"
                    loading="lazy"
                  />
                </div>
              </div>

              <div className="border-t border-brand-primary/15 p-8">
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brand-primary">
                  {mode.eyebrow}
                </p>
                <h3 className="mt-3 text-2xl font-semibold text-neutral-900">
                  {mode.title}
                </h3>
                <p className="mt-4 text-base leading-relaxed text-neutral-600">
                  {mode.description}
                </p>

                {mode.kind === 'tap' ? (
                  <div className="mt-6">
                    <AppStoreBadges
                      playStoreUrl={PLAY_STORE_URL}
                      appStoreUrl={APP_STORE_URL}
                      size="sm"
                    />
                  </div>
                ) : mode.kind === 'checkout' ? (
                  <div className="mt-6 rounded-2xl bg-[#fff7ef] p-4 text-sm leading-relaxed text-neutral-700">
                    Mix cash, card, and rewards on one sale. {existingPosNote}
                  </div>
                ) : (
                  <div className="mt-6 rounded-2xl bg-[#fff7ef] p-4 text-sm leading-relaxed text-neutral-700">
                    Full appointment booking is live — customers pick a time,
                    you see it next to the rest of the shop.
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductModesSection;
